import React, { useCallback, useEffect } from 'react';

import { Input, Radio } from '@dogtainers/dgt-blocks/src/components/form';
import { Grid, Typography } from '@material-ui/core';

import {
  LocationResponse,
  findAddressProperty,
  getGooglePlaceDetails,
} from '../../../utils/googleMap.utils';
import { PageFormProps } from '../booking.types';
import useStyles from './styles';

const radioToggleItems = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

export const BookingPage3: React.FC<PageFormProps> = ({
  values: { sender, departure },
  handleChange,
  errors: { sender: senderErrors, departure: departureErrors },
  isValueEqual,
  setFieldValue,
  handleBlur,
  touched,
  isErrorsVisible,
  isFieldInFocus,
  children,
}) => {
  const { pageTitle, pageSubTitle, subTitleDetails } = useStyles();

  const prefillAddressFields = useCallback(
    (location: LocationResponse) => {
      if (location.address_components) {
        const addressComponents = location.address_components;

        setFieldValue(
          'departure.pickupAddress.streetAddress',
          findAddressProperty(addressComponents, 'route'),
        );

        setFieldValue(
          'departure.pickupAddress.city',
          findAddressProperty(addressComponents, 'locality') ||
            findAddressProperty(addressComponents, 'postal_town'),
        );

        setFieldValue(
          'departure.pickupAddress.state',
          findAddressProperty(addressComponents, 'country'),
        );

        setFieldValue(
          'departure.pickupAddress.postcode',
          findAddressProperty(addressComponents, 'postal_code'),
        );
      }
    },
    [setFieldValue],
  );

  useEffect(() => {
    const fetchAddressDetails = async (placeId: string) => {
      const details = await getGooglePlaceDetails(placeId);
      prefillAddressFields(details);
    };

    if (
      departure?.pickupAddress?.placeId &&
      !departure?.pickupAddress?.streetAddress
    )
      fetchAddressDetails(departure.pickupAddress.placeId);
  }, [prefillAddressFields]);

  return (
    <Grid container justifyContent="center" spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h4" className={pageTitle}>
          Booking Form
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" className={pageSubTitle}>
          Sender Details
          <span className={subTitleDetails}>
            Who will be dropping your pet off?
          </span>
        </Typography>
      </Grid>
      {children}
      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Input
              label="First Name"
              name="sender.nameFirst"
              placeholder="Name here"
              onChange={handleChange}
              value={sender?.nameFirst}
              error={senderErrors?.nameFirst}
              onBlur={handleBlur}
              touched={touched.sender?.nameFirst}
              isErrorVisible={isErrorsVisible}
              innerRef={isFieldInFocus?.('sender.nameFirst')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              label="Last Name"
              name="sender.nameLast"
              placeholder="Last name here"
              onChange={handleChange}
              value={sender?.nameLast}
              error={senderErrors?.nameLast}
              onBlur={handleBlur}
              touched={touched.sender?.nameLast}
              isErrorVisible={isErrorsVisible}
              innerRef={isFieldInFocus?.('sender.nameLast')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              label="Phone Number"
              name="sender.phone"
              placeholder="e.g. +61 888 888 888 "
              onChange={handleChange}
              value={sender?.phone}
              error={senderErrors?.phone}
              onBlur={handleBlur}
              touched={touched.sender?.phone}
              isErrorVisible={isErrorsVisible}
              innerRef={isFieldInFocus?.('sender.phone')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              label="Email"
              name="sender.email"
              placeholder="email@contents.com"
              onChange={handleChange}
              value={sender?.email}
              error={senderErrors?.email}
              onBlur={handleBlur}
              touched={touched.sender?.email}
              isErrorVisible={isErrorsVisible}
              innerRef={isFieldInFocus?.('sender.email')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Radio
              label="Do you require collection?"
              name="departure.isPickup"
              value={departure?.isPickup}
              error={String(departureErrors?.isPickup)}
              onChange={handleChange}
              menuItems={radioToggleItems}
              isErrorVisible={isErrorsVisible}
              innerRef={isFieldInFocus?.('sender.isPickup')}
            />
          </Grid>

          {isValueEqual('departure.isPickup', true) && (
            <>
              <Grid item xs={12} md={6}>
                <Radio
                  label="Will you be completing home quarantine?"
                  name="departure.pickupAddress.isCompletingHomeQuarantine"
                  value={departure?.pickupAddress?.isCompletingHomeQuarantine}
                  error={String(
                    departureErrors?.pickupAddress?.isCompletingHomeQuarantine,
                  )}
                  onChange={handleChange}
                  menuItems={radioToggleItems}
                  isErrorVisible={isErrorsVisible}
                  innerRef={isFieldInFocus?.(
                    'departure.pickupAddress.isCompletingHomeQuarantine',
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  label="Street Address"
                  name="departure.pickupAddress.streetAddress"
                  placeholder="e.g. 1 Name St, Place"
                  onChange={handleChange}
                  value={departure?.pickupAddress?.streetAddress}
                  error={departureErrors?.pickupAddress?.streetAddress}
                  onBlur={handleBlur}
                  touched={touched.departure?.pickupAddress?.streetAddress}
                  isErrorVisible={isErrorsVisible}
                  innerRef={isFieldInFocus?.(
                    'departure.pickupAddress.streetAddress',
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  label="Street Address Line 2"
                  name="departure.pickupAddress.streetAddress2"
                  placeholder="e.g. Unit 2"
                  onChange={handleChange}
                  value={departure?.pickupAddress?.streetAddress2}
                  error={departureErrors?.pickupAddress?.streetAddress2}
                  onBlur={handleBlur}
                  touched={touched.departure?.pickupAddress?.streetAddress2}
                  isErrorVisible={isErrorsVisible}
                  innerRef={isFieldInFocus?.(
                    'departure.pickupAddress.streetAddress2',
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  label="City"
                  name="departure.pickupAddress.city"
                  onChange={handleChange}
                  placeholder="City"
                  value={departure?.pickupAddress?.city}
                  error={departureErrors?.pickupAddress?.city}
                  onBlur={handleBlur}
                  touched={touched.departure?.pickupAddress?.city}
                  isErrorVisible={isErrorsVisible}
                  innerRef={isFieldInFocus?.('departure.pickupAddress.city')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  label="State"
                  name="departure.pickupAddress.state"
                  onChange={handleChange}
                  placeholder="State"
                  value={departure?.pickupAddress?.state}
                  error={departureErrors?.pickupAddress?.state}
                  onBlur={handleBlur}
                  touched={touched.departure?.pickupAddress?.state}
                  isErrorVisible={isErrorsVisible}
                  innerRef={isFieldInFocus?.('departure.pickupAddress.state')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  label="Post Code"
                  name="departure.pickupAddress.postcode"
                  placeholder="8888"
                  onChange={handleChange}
                  value={departure?.pickupAddress?.postcode}
                  error={String(departureErrors?.pickupAddress?.postcode ?? '')}
                  onBlur={handleBlur}
                  touched={touched.departure?.pickupAddress?.postcode}
                  isErrorVisible={isErrorsVisible}
                  innerRef={isFieldInFocus?.(
                    'departure.pickupAddress.postcode',
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
